import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import {
  PROFILE_SCORE_MAX,
  PROFILE_SCORE_NOTES,
  PROFILE_SCORE_SIGNALS,
  PROFILE_SCORE_TIERS,
  formatTierRange,
  maxPossibleProfileScore,
} from '../../lib/profileScore';

const sourceLabel: Record<string, string> = {
  subscriber: 'Subscriber',
  connection: 'Connection (by email)',
  transcript: 'Transcripts',
};

export default function ProfileScoreSystemView() {
  const maxPossible = maxPossibleProfileScore();

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Link
        to="/subscriber-field-definitions"
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Field definitions
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-2">
        <BarChart3 className="h-7 w-7 text-emerald-500" />
        Profile scoring
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
        Each subscriber gets a 0–{PROFILE_SCORE_MAX} richness score used for AI email personalization and
        quick triage. Points stack from the signals below (capped at {PROFILE_SCORE_MAX}).
      </p>

      <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Scoring signals
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Maximum if all apply: {maxPossible} (stored total is min(sum, {PROFILE_SCORE_MAX}))
          </p>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {PROFILE_SCORE_SIGNALS.map((signal) => (
            <div
              key={signal.id}
              className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:gap-6 px-6 py-4 items-start"
            >
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">{signal.label}</span>
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {sourceLabel[signal.source] ?? signal.source}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{signal.description}</p>
                <p className="text-xs font-mono text-gray-400 mt-1">{signal.id}</p>
              </div>
              <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400 sm:text-right tabular-nums">
                +{signal.points}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Tiers
          </h2>
        </div>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {PROFILE_SCORE_TIERS.map((tier) => (
            <li key={tier.tier} className="px-6 py-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium text-gray-900 dark:text-white capitalize">
                  {tier.tier}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                  {formatTierRange(tier)}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{tier.description}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Custom field weights
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Configured per field on{' '}
            <Link to="/subscriber-field-definitions" className="text-emerald-600 dark:text-emerald-400 hover:underline">
              Field definitions
            </Link>
            . Shown as <code className="text-[10px]">custom:field_key</code> in the breakdown.
          </p>
        </div>
        <p className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
          Each active field can add 0–100 points when it has a filled value on the subscriber.
        </p>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">
          Notes
        </h2>
        <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 dark:text-gray-400">
          {PROFILE_SCORE_NOTES.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
